import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../components/EntityTable/surfaces.ts"
import { TableResultCount } from "../../../../components/Table/TableResultCount.tsx"
import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import { useInfiniteScroll } from "../../../../hooks/use-table-infinite-scroll.tsx"
import type { AccountDeletionRequest } from "../../types/types.ts"
import { getActions } from "./column-actions.tsx"
import { getColumns } from "./columns.tsx"

interface RequestsTableProps {
  data: AccountDeletionRequest[]
  loading: boolean
  canManage: boolean
  deciding?: boolean
  onApprove: (r: AccountDeletionRequest) => void
  onReject: (r: AccountDeletionRequest) => void
  onRetry: (r: AccountDeletionRequest) => void
  next?: string | null
  retryNext?: string | null
  total: number
  onLoadMore: () => void
}

export const RequestsTable = ({
  data,
  loading,
  canManage,
  deciding,
  onApprove,
  onReject,
  onRetry,
  next,
  retryNext,
  total,
  onLoadMore
}: RequestsTableProps) => {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  const { onTableScroll, containerRef } = useInfiniteScroll({
    loading,
    hasMore: !!next,
    loadMoreKey: next,
    loadMore: onLoadMore
  })
  const columns = [
    ...getColumns(t, formatDate),
    getActions({ t, canManage, deciding, onApprove, onReject, onRetry })
  ]

  return (
    <div ref={containerRef}>
      <EntityTable<AccountDeletionRequest>
        surface={TABLE_SURFACES.accountDeletionRequests}
        selectionMode="on-demand"
        tab={DEFAULT_TAB}
        rowKey="id"
        loading={loading && !data?.length}
        dataSource={data}
        columns={columns}
        scroll={{ y: 520 }}
        onScroll={onTableScroll}
        withOutPagination
        pagination={false}
      />
      <TableResultCount
        loaded={data?.length ?? 0}
        total={total}
        loading={loading}
        next={next}
        retryNext={retryNext}
        onRetry={onLoadMore}
      />
    </div>
  )
}
